import React, { useState } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

const MemberSearchTab = ({ user }) => {
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null); // identifier of selected user

  const handleSearch = async () => {
    if (query.length < 2) return;
    setLoading(true);
    try {
      const res = await adminFetch(
        `${API_URL}/api/admin/members/search?q=${encodeURIComponent(query)}`
      );
      const data = await res.json();
      if (data.success) setMembers(data.members);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Search by @username, name, or family..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: '8px',
            border: '1px solid #d1d5db', fontSize: '14px'
          }}
        />
        <button
          onClick={handleSearch}
          disabled={loading || query.length < 2}
          style={{
            padding: '10px 20px', borderRadius: '8px', border: 'none',
            background: '#6366f1', color: 'white', cursor: 'pointer',
            fontSize: '14px', fontWeight: 500, opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {searched && members.length === 0 && (
        <p style={{ color: '#6b7280', textAlign: 'center', padding: '24px' }}>No members found</p>
      )}

      {members.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="admin2-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Tg ID</th>
                <th>Status</th>
                <th>Week</th>
                <th>Next</th>
                <th>Context</th>
                <th>GDPR</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr
                  key={m.airtable_id || m.tg_id}
                  onClick={() => setSelected(m.tg_username || m.tg_id)}
                  style={{ cursor: 'pointer' }}
                  title="Click to view full history"
                >
                  <td style={{ fontWeight: 500, color: '#6366f1' }}>@{m.tg_username || '—'}</td>
                  <td>{m.name} {m.family || ''}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{m.tg_id || '—'}</td>
                  <td><StatusBadge value={m.status} /></td>
                  <td><StatusBadge value={m.current_week_status} /></td>
                  <td><StatusBadge value={m.next_week_status} /></td>
                  <td style={{ fontSize: '12px' }}>{m.matching_context || 'global'}</td>
                  <td>{m.consent_gdpr ? '✓' : '✗'}</td>
                  <td style={{ fontSize: '12px' }}>{m.created_at?.split('T')[0] || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
            {members.length} result{members.length !== 1 ? 's' : ''} — click a row to see full history
          </p>
        </div>
      )}

      {selected && (
        <MemberHistoryModal
          identifier={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

const MemberHistoryModal = ({ identifier, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminFetch(
          `${API_URL}/api/admin/members/${encodeURIComponent(identifier)}/history`
        );
        const json = await res.json();
        if (cancelled) return;
        if (json.success) setData(json);
        else setError(json.message || 'Failed to load history');
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [identifier]);

  // Build interleaved timeline of matches and events
  const timeline = React.useMemo(() => {
    if (!data) return [];
    const items = [];
    (data.matches || []).forEach(m => items.push({
      kind: 'match', date: m.created_at || m.week_start, payload: m
    }));
    (data.events || []).forEach(e => items.push({
      kind: 'event', date: e.created_at, payload: e
    }));
    return items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [data]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 20px', zIndex: 100, overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: '12px', padding: '24px',
          maxWidth: '900px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>Member History</h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', fontSize: '24px',
              cursor: 'pointer', color: '#6b7280', padding: '4px 12px',
            }}
          >×</button>
        </div>

        {loading && <p style={{ color: '#6b7280' }}>Loading history…</p>}
        {error && <p style={{ color: '#dc2626' }}>Error: {error}</p>}

        {data && data.member && (
          <>
            <ProfileBlock m={data.member} />
            <SectionHeader>Matches ({data.counts.matches})</SectionHeader>
            <MatchesList matches={data.matches} />
            <SectionHeader>Timeline ({timeline.length})</SectionHeader>
            <Timeline items={timeline} />
          </>
        )}
      </div>
    </div>
  );
};

const ProfileBlock = ({ m }) => (
  <div style={{
    background: '#f9fafb', borderRadius: '8px', padding: '16px', marginBottom: '20px',
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px 16px',
    fontSize: '13px',
  }}>
    <Field label="Name">{m.name} {m.family || ''}</Field>
    <Field label="Username">@{m.tg_username || '—'}</Field>
    <Field label="Tg ID"><code>{m.tg_id || '—'}</code></Field>
    <Field label="Email">{m.email || '—'}</Field>
    <Field label="Status"><StatusBadge value={m.status} /></Field>
    <Field label="GDPR">{m.consent_gdpr ? '✓' : '✗'}</Field>
    <Field label="Country">{m.country?.name_en || '—'}</Field>
    <Field label="City">{m.city?.name_en || '—'}</Field>
    <Field label="Time zone">{m.time_zone || '—'}</Field>
    <Field label="Languages">{(m.languages || []).join(', ') || '—'}</Field>
    <Field label="Profession">{m.profession || '—'} {m.grade ? `(${m.grade})` : ''}</Field>
    <Field label="Matching context">{m.matching_context || 'global'}</Field>
    <Field label="Current week"><StatusBadge value={m.current_week_status} /></Field>
    <Field label="Next week"><StatusBadge value={m.next_week_status} /></Field>
    <Field label="Registered">{m.created_at?.split('T')[0] || '—'}</Field>
    <Field label="Last seen">{m.last_seen?.split('T')[0] || '—'}</Field>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ marginTop: '2px' }}>{children}</div>
  </div>
);

const SectionHeader = ({ children }) => (
  <h3 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', marginTop: '20px', marginBottom: '8px' }}>{children}</h3>
);

const MatchesList = ({ matches }) => {
  if (!matches || matches.length === 0) return <p style={{ color: '#9ca3af' }}>No matches yet</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {matches.map(m => (
        <div key={m.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <strong>{m.week_start}</strong>
              {m.community?.name && <span style={{ marginLeft: '8px', color: '#6b7280' }}>· {m.community.name}</span>}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <StatusBadge value={m.status} />
              <StatusBadge value={m.notifications} />
              {m.we_met_self && <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '10px', background: '#eef2ff', color: '#4338ca' }}>met: {m.we_met_self}</span>}
              {m.feedback_self && <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '10px', background: '#fef3c7', color: '#92400e' }}>★ {m.feedback_self}</span>}
            </div>
          </div>
          <div style={{ marginTop: '4px', color: '#374151' }}>
            with @{m.partner?.tg_username || '—'} ({m.partner?.name || '—'})
          </div>
        </div>
      ))}
    </div>
  );
};

const Timeline = ({ items }) => {
  if (!items || items.length === 0) return <p style={{ color: '#9ca3af' }}>No events</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {items.map((it, i) => it.kind === 'match' ? (
        <TimelineRow key={`m-${it.payload.id}`} date={it.date} icon="🤝" label="Match created"
          detail={`${it.payload.week_start} · with @${it.payload.partner?.tg_username || '—'} · ${it.payload.status}`} />
      ) : (
        <TimelineRow key={`e-${it.payload.id || i}`} date={it.date}
          icon={categoryIcon(it.payload)}
          label={`${it.payload.category || it.payload.event || 'Event'}${it.payload.source_script ? ` · ${it.payload.source_script}` : ''}`}
          detail={it.payload.message_content || '—'} />
      ))}
    </div>
  );
};

const categoryIcon = (e) => {
  if (e.category === 'Message') return '📤';
  if (e.category === 'Reply') return '📥';
  if (e.event === 'Activated') return '🟢';
  if (e.event === 'Deactivated') return '🔴';
  return '•';
};

const TimelineRow = ({ date, icon, label, detail }) => (
  <div style={{ display: 'flex', gap: '10px', padding: '8px 10px', border: '1px solid #f3f4f6', borderRadius: '6px', fontSize: '13px' }}>
    <div style={{ fontSize: '16px', flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '2px' }}>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <span style={{ color: '#9ca3af', fontSize: '11px', whiteSpace: 'nowrap' }}>{formatDate(date)}</span>
      </div>
      <div style={{ color: '#4b5563', fontSize: '12px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {detail}
      </div>
    </div>
  </div>
);

const formatDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return d; }
};

const StatusBadge = ({ value }) => {
  if (!value) return <span style={{ color: '#d1d5db' }}>—</span>;
  const colors = {
    Admin: '#7c3aed', EarlyBird: '#f59e0b', Free: '#6b7280',
    PRO: '#3b82f6', Premium: '#10b981', Matched: '#10b981',
    Active: '#10b981', Passive: '#9ca3af',
    Sent: '#10b981', Pending: '#f59e0b', Partial: '#f97316',
    Unmatched: '#ef4444',
  };
  const bg = colors[value] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 500, color: 'white', background: bg, whiteSpace: 'nowrap'
    }}>
      {value}
    </span>
  );
};

export default MemberSearchTab;
